/**
 * useDocIndex — project|type|ref -> attachment id, behind the clickable quote /
 * PO / invoice numbers in the bid tables.
 *
 * ONE small fetch of every labeled file instead of a request per row. An
 * opportunity and a project are the same `projects` row, so the Projects table
 * and the Opportunities table share this one index and can never disagree about
 * what a number links to.
 *
 * Uploading or relabeling a file calls notifyDocsChanged(), so a number turns
 * into a link the moment the Doc Hub closes — no page refresh. (Before this
 * existed the Projects tab only picked up new files on a remount.)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { normalizeDocRef } from '../utils/attachmentPreview';
import { useApiBase } from '../context/DataScope';
import { latestReads, useLiveRefresh } from '../live/useLive';

const listeners = new Set();
// attachment id -> file name, so a table number can say on hover exactly which
// file it opens. Module-level: every table shares the one index.
const names = new Map();

/** File name behind an attachment id from the index, or ''. */
export const docFileName = (attId) => names.get(attId) || '';

/** Tell every open table to re-pull the index. Called by the Doc Hub. */
export const notifyDocsChanged = () => {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a dead listener must not stop the rest */
    }
  });
};

export function useDocIndex() {
  // On the KSA tab this is the KSA Doc Hub's own index, never the main one.
  const base = useApiBase();
  const [index, setIndex] = useState(null);
  const [tick, setTick] = useState(0);
  // Reads overlap (an upload here, then someone else's change a moment
  // later). A newer read must not cancel an older one: if the newer one fails,
  // the older one may be the only answer with the new file. latestReads lets
  // each answer land unless a newer one is already on screen.
  const reads = useRef(null);
  if (!reads.current) reads.current = latestReads();
  const current = useRef({ base, mounted: true });
  current.current.base = base;
  useEffect(
    () => () => {
      current.current.mounted = false;
    },
    []
  );

  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    listeners.add(reload);
    return () => {
      listeners.delete(reload);
    };
  }, [reload]);
  // Someone else's upload, relabel or delete turns a number into a link (or
  // back) here too. Main scope only: the KSA tab's provider already calls
  // notifyDocsChanged for its own files.
  useLiveRefresh(['projects', 'attachments'], reload, {
    match: (e) => base === '/api' && (e.kind === 'attachments' || e.sub === 'attachments' || e.sub === 'folders'),
  });

  useEffect(() => {
    const ticket = reads.current.start();
    (async () => {
      try {
        const r = await fetch(`${base}/attachments/index`);
        if (!r.ok) return; // a failed read keeps what is shown
        const list = await r.json();
        // gone, or switched to the other Doc Hub (KSA tab) meanwhile
        if (!current.current.mounted || current.current.base !== base) return;
        if (!reads.current.show(ticket)) return; // something newer is already on screen
        const map = new Map();
        for (const a of list) {
          const key = `${a.project_id}|${a.doc_type}|${normalizeDocRef(a.doc_ref)}`;
          // first wins — the server orders them: the file a person linked,
          // else a PDF (previewable), else the newest (see pickDocFor)
          if (!map.has(key)) map.set(key, a.id);
          if (a.filename) names.set(a.id, a.filename);
        }
        setIndex(map);
      } catch {
        /* offline/dev — the numbers just stay plain text */
      }
    })();
  }, [tick, base]);

  /** attachment id for one number, or undefined when nothing is attached. */
  const docIdFor = (projectId, type, ref) => {
    const n = normalizeDocRef(ref);
    return index && n ? index.get(`${projectId}|${type}|${n}`) : undefined;
  };

  return docIdFor;
}
